import { FocusTrap } from 'focus-trap-react';
import type { CSSProperties, ReactNode } from 'react';
import { useRef } from 'react';
import { styled } from 'styled-components';

import { H2 } from '#/components/Text/H2';
import { useOnWindowKeyDown } from '#/hooks/useOnWindowKeyDown';
import { colors } from '#/theme/theme';

import type { ModalDismissalReason } from './ModalDismissalReason';

export const modalBaseInset = '16px';

export const ModalBaseOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  opacity: 0.85;
  background-color: ${colors.mediumContentGrey};
`;

const Content = styled.div<{ $inlineOverflow?: CSSProperties['overflow'] }>`
  position: fixed;
  display: flex;
  align-items: flex-start;
  inset: 0;
  z-index: 10000;
  overflow: ${(props) => props.$inlineOverflow || 'auto'} auto;
  text-align: center;
  -webkit-overflow-scrolling: touch;
  padding: ${modalBaseInset};
  cursor: pointer;
`;

export const defaultDialogBorderRadius = '4px';
const DialogWrapper = styled.div`
  position: relative;
  outline: 0;
  width: 100%;
  background-color: white;
  border-radius: ${defaultDialogBorderRadius};
  box-shadow: 0 3px 8px 0 rgb(0 0 0 / 0.4);
  display: block;
  text-align: left;
  box-sizing: border-box;
  max-width: 520px;
  margin: auto;
  cursor: default;
  color: ${colors.mediumContentGrey};
  line-height: 1.5;
`;

export function Dialog({
  className,
  children,
  'data-qa-id': qaId,
}: {
  className?: string;
  children: ReactNode;
  'data-qa-id'?: string;
}) {
  return (
    <DialogWrapper
      className={className}
      // Prevent clicks from going through to the backdrop
      onClick={(event) => event.stopPropagation()}
      data-qa-id={qaId}
    >
      {children}
    </DialogWrapper>
  );
}

export function ModalTitle({
  children,
  id,
  ignorePadding,
}: {
  children: ReactNode;
  id?: string;
  ignorePadding?: boolean;
}) {
  return (
    <H2 id={id} styleAs="h3" pb={ignorePadding ? undefined : 2}>
      {children}
    </H2>
  );
}

type Props = {
  className?: string | undefined;
  onClose?: (reason: ModalDismissalReason) => void;
  children: ReactNode;
  'aria-labelledby'?: string;
  inlineOverflow?: CSSProperties['overflow'];
  customOverlay?: ReactNode;
};

export function ModalBase({
  className,
  onClose,
  children,
  'aria-labelledby': ariaLabeledBy,
  inlineOverflow = 'auto',
  customOverlay,
}: Props) {
  const lastClosedAt = useRef<number>(0);
  const throttleDuration = 1000;

  // useOnWindowKeyDown is not throttled which means that multiple keydown
  // events will be fired on a single key press
  const onKeyboardClose = (reason: ModalDismissalReason) => {
    const now = Date.now();
    if (now - lastClosedAt.current < throttleDuration) return;
    lastClosedAt.current = now;
    onClose?.(reason);
  };

  useOnWindowKeyDown(['Escape'], (e) =>
    onKeyboardClose({ type: 'press-escape', originalEvent: e }),
  );

  return (
    <>
      {customOverlay || <ModalBaseOverlay />}

      <FocusTrap
        focusTrapOptions={{
          tabbableOptions: { displayCheck: 'none' },
        }}
      >
        <Content
          className={className}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={ariaLabeledBy}
          id="idealist-modal-container"
          $inlineOverflow={inlineOverflow}
        >
          {children}
        </Content>
      </FocusTrap>
    </>
  );
}
